import moment from 'moment';
import { RECEIVED_WITHIN_OPTIONS } from './billsFilters';
import { ALLOCATION_FILTER_OPTIONS, AP_PUSH_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from './billsQuery';

/**
 * What the Bills page can be filtered by, in one place (LB-6087): the filter panel lists these, the column funnels open
 * it at one of them, and the chips under the search box name what is picked in them.
 *
 * Pure on purpose, like billsQuery.js: the wording and the toggling are the parts worth testing outright.
 *
 * `multi` filters take any number of values and `single` ones one at a time. Those with a `searchLabel` can run long,
 * so their list gets a search box: a big company has hundreds of service providers and properties. A `dateRange` is two
 * filters picked together on a calendar, its first and last day.
 */
export const FILTER_CATEGORIES = [{
  key: 'statuses',
  label: 'Status',
  kind: 'multi'
}, {
  key: 'allocationStates',
  label: 'Allocation',
  kind: 'multi'
},
// AppFolio's alone (LB-6031): other companies have no AP Push column, and the list ignores the filter for them.
{
  key: 'apPushEnrolment',
  label: 'AP Push',
  kind: 'single',
  isAppfolioOnly: true
}, {
  key: 'providers',
  label: 'Service provider',
  kind: 'multi',
  searchLabel: 'Search service providers'
}, {
  key: 'properties',
  label: 'Property',
  kind: 'multi',
  searchLabel: 'Search properties',
  emptyText: 'This company has no properties yet.'
}, {
  key: 'utilities',
  label: 'Utility',
  kind: 'multi'
}, {
  key: 'receivedWithin',
  label: 'Received',
  kind: 'single'
}, {
  key: 'servicePeriod',
  label: 'Service period',
  kind: 'dateRange',
  fromKey: 'servicePeriodFrom',
  toKey: 'servicePeriodTo'
}];
const categoryOf = key => FILTER_CATEGORIES.find(category => category.key === key);

/** The categories this company can use. */
export function visibleFilterCategories(_ref) {
  let showApPush = _ref.showApPush;
  return FILTER_CATEGORIES.filter(category => showApPush || !category.isAppfolioOnly);
}

/**
 * The options one category offers, as `{ value, label }`.
 *
 * `options` carries the three lists the page builds itself: the company's properties, the service's providers and the
 * fixed utilities. A date range offers none: it is picked on a calendar.
 */
export function categoryOptions(key, options) {
  switch (key) {
    case 'statuses':
      return STATUS_FILTER_OPTIONS;
    case 'allocationStates':
      return ALLOCATION_FILTER_OPTIONS;
    case 'apPushEnrolment':
      return AP_PUSH_FILTER_OPTIONS;
    case 'providers':
      return options.providers;
    case 'properties':
      return options.properties;
    case 'utilities':
      return options.utilities;
    case 'receivedWithin':
      return RECEIVED_WITHIN_OPTIONS;
    default:
      return [];
  }
}

/** What is picked in one category, as a list whichever kind it is: a date range's ends that are set, first day first. */
export function pickedValues(filters, key) {
  const category = categoryOf(key);
  if ((category === null || category === void 0 ? void 0 : category.kind) === 'dateRange') return [filters[category.fromKey], filters[category.toKey]].filter(Boolean);
  const value = filters[key];
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

/** The filters with one category emptied. */
export function withCategoryCleared(filters, key) {
  const category = categoryOf(key);
  if ((category === null || category === void 0 ? void 0 : category.kind) === 'dateRange') return {
    [category.fromKey]: '',
    [category.toKey]: ''
  };
  return {
    [key]: Array.isArray(filters[key]) ? [] : ''
  };
}
const DAY = 'YYYY-MM-DD';
const asDay = (value, format) => moment(value, DAY, true).format(format);

/**
 * A service period in words: "Sep 1, 2026 – Sep 30, 2026", one day as itself, and an end a link left open as
 * "From …" or "Until …". `short` drops the year, for the panel's category list.
 */
export function describeServicePeriod(from, to) {
  let _ref2 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
    _ref2$short = _ref2.short,
    short = _ref2$short === void 0 ? false : _ref2$short;
  // Short drops the year only when both ends share one; across years it would read as the wrong range.
  const sameYear = !from || !to || from.slice(0, 4) === to.slice(0, 4);
  const format = short && sameYear ? 'MMM D' : 'MMM D, YYYY';
  if (from && to) return from === to ? asDay(from, format) : "".concat(asDay(from, format), " \u2013 ").concat(asDay(to, format));
  if (from) return "From ".concat(asDay(from, format));
  if (to) return "Until ".concat(asDay(to, format));
  return '';
}

/**
 * The filters after one option is clicked.
 *
 * A `multi` value is toggled and kept in the order the options are offered, so the same choice reads the same in the
 * address and the chip however it was made; anything picked that is not offered (a provider a link named, say) stays
 * after them rather than being dropped. A `single` value is set.
 */
export function withOptionToggled(filters, category, value, offered) {
  if (category.kind === 'single') return {
    [category.key]: value
  };
  const current = filters[category.key];
  const next = current.includes(value) ? current.filter(picked => picked !== value) : [...current, value];
  const offeredValues = offered.map(option => option.value);
  return {
    [category.key]: [...offeredValues.filter(candidate => next.includes(candidate)), ...next.filter(picked => !offeredValues.includes(picked))]
  };
}

/** A property the company list does not have: deleted since, or a link from another company. */
export const UNKNOWN_PROPERTY = 'Unknown property';
const labelOf = (key, value, offered) => {
  var _offered$find;
  const found = (_offered$find = offered.find(option => option.value === value)) === null || _offered$find === void 0 ? void 0 : _offered$find.label;
  if (found) return found;
  return key === 'properties' ? UNKNOWN_PROPERTY : value;
};

/**
 * One chip per category in use, in the panel's order: `{ key, label, text }`, read as "Status: Needs review, Error".
 *
 * Two values are named; more are named by the first and a count, so a long pick does not push the table down a line
 * per value. AP Push is left out where the company has no AP Push, since the list ignores it there.
 */
export function filterChips(filters, options, _ref3) {
  let showApPush = _ref3.showApPush;
  return visibleFilterCategories({
    showApPush
  }).map(category => {
    const picked = pickedValues(filters, category.key);
    if (!picked.length) return null;
    if (category.kind === 'dateRange') {
      const text = describeServicePeriod(filters[category.fromKey], filters[category.toKey]);
      return {
        key: category.key,
        label: category.label,
        text
      };
    }
    const offered = categoryOptions(category.key, options);
    const names = picked.map(value => labelOf(category.key, value, offered));
    const text = names.length <= 2 ? names.join(', ') : "".concat(names[0], " +").concat(names.length - 1);
    return {
      key: category.key,
      label: category.label,
      text
    };
  }).filter(Boolean);
}

/**
 * How many filters differ between two sets: the panel's draft and what the list shows. Compared as the panel shows
 * them, so picking a value and unpicking it again is no change, and order within a filter does not count.
 */
export function changedCategoryCount(draft, applied, _ref4) {
  let showApPush = _ref4.showApPush;
  // A date range's two ends compared in place; sorting them would make a range from a day and one until it equal.
  const asKey = (filters, key) => {
    const category = categoryOf(key);
    if ((category === null || category === void 0 ? void 0 : category.kind) === 'dateRange') return "".concat(filters[category.fromKey], "|").concat(filters[category.toKey]);
    return [...pickedValues(filters, key)].sort().join('\u0000');
  };
  return visibleFilterCategories({
    showApPush
  }).filter(category => asKey(draft, category.key) !== asKey(applied, category.key)).length;
}